import { DownloadOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Tooltip } from 'components/ui';
import {
  ErrorText,
  SuccessText,
} from 'horizon/components/Assets/CreateAssetsModal/ModalContent/common.style';
import { type ImportResult } from 'horizon/routes/Inspections/InspectionDetail/InspectionDamageIngestion/types';
import { useCreateSignedGetMutation } from 'types/atlas-graphql';

type ErrorStateProps = {
  importResult: ImportResult | undefined;
  importedFileName: string;
};

export const ErrorState: React.FunctionComponent<ErrorStateProps> = ({
  importResult,
  importedFileName,
}) => {
  const { t } = useTranslation();
  const [signedUrl, setSignedUrl] = useState<string | undefined>();
  const [createSignedGet] = useCreateSignedGetMutation();

  const { createdDamageCount, duplicateDamageCount, invalidDamageCount, invalidDamageCsv } =
    importResult ?? {};

  useEffect(() => {
    if (invalidDamageCsv) {
      setSignedUrl(signedUrl);
      fetchSignedUrl();
    } else {
      setSignedUrl(undefined);
    }
  }, [invalidDamageCsv]);

  const fetchSignedUrl = async () => {
    const s3Key = getIngestS3Key();
    try {
      if (s3Key) {
        const { data } = await createSignedGet({
          variables: { input: { ingestS3Key: s3Key } },
        });
        const signedUrl = data?.createSignedGet?.url;
        if (signedUrl) {
          setSignedUrl(signedUrl);
        }
      }
    } catch {
      setSignedUrl(undefined);
    }
  };

  const getIngestS3Key = () => {
    if (!invalidDamageCsv) {
      return undefined;
    }
    try {
      return decodeURIComponent(new URL(invalidDamageCsv).pathname.slice(1));
    } catch {
      return undefined;
    }
  };

  if (importResult?.errorMessage) {
    return (
      <>
        <ErrorText fontWeight="bold" mb={2}>
          {importResult.errorMessage}
        </ErrorText>
      </>
    );
  }

  return (
    <>
      <SuccessText>
        {t('inspection.damagesSuccessfullyCreated', { count: createdDamageCount ?? 0 })}
      </SuccessText>
      {!!duplicateDamageCount && (
        <>
          <ErrorText fontWeight="bold" mb={2}>
            {t('inspection.rowsContainedDuplicateData', {
              count: duplicateDamageCount,
              filename: importedFileName,
            })}
          </ErrorText>
        </>
      )}
      {!!invalidDamageCount && invalidDamageCsv && (
        <>
          <ErrorText fontWeight="bold" mb={2}>
            {t('assets.rowsContainedInvalidData', {
              count: invalidDamageCount,
              filename: importedFileName,
            })}
          </ErrorText>
          <ErrorText>
            {t(
              'inspection.downloadErrorFileInstructions',
              'Please use the button below to download the error file, review and complete faulty or missing data and upload again.'
            )}
          </ErrorText>
          <Tooltip
            title={
              !signedUrl
                ? t('inspection.cannotGenerateErrorFileLink', {
                    filename: importedFileName,
                  })
                : null
            }
          >
            <Button
              icon={<DownloadOutlined />}
              href={signedUrl}
              target="_blank"
              rel="noopener noreferrer"
              disabled={!signedUrl}
            >
              {t('assets.downloadErrorFile', 'Download Error File')}
            </Button>
          </Tooltip>
        </>
      )}
    </>
  );
};
